/**
 * URLs dinâmicas do painel — API do backend e webhook do suporte (n8n).
 *
 * DOIS docs no Firestore, escritos pelo backend (systemRoutes /app-config):
 *
 *   settings/publicConfig = { apiUrl }                      ← legível SEM login
 *   settings/appConfig    = { apiUrl, chatWebhookUrl }      ← exige login
 *
 * O par existe por causa das telas PÚBLICAS do painel (landing, cardápio do cliente,
 * /rastreio, checkout do carrinho): elas chamam a API antes de existir usuário, e as
 * regras do Firestore exigem auth em `settings/*`. Então a `apiUrl` — que já é visível
 * em qualquer requisição de rede — mora também num doc público, enquanto o webhook do
 * n8n fica só no doc autenticado (o chat de suporte só roda logado). O `appConfig` é o
 * mesmo doc que o app do motoboy (config/env.js) lê.
 *
 * Regras (pedido do Filipe, 06/09/2026):
 *  - NÃO ficar consultando: no máximo UMA leitura de cada doc por carregamento, e só
 *    quando o cache local não existe ou está velho (CACHE_TTL_MS). Nada de listener.
 *  - Salvar localmente: o último valor bom vai pro localStorage, então o próximo
 *    F5 já nasce com a URL certa sem tocar na rede.
 *  - NÃO mostrar em lugar nenhum: nenhum log imprime as URLs — só "carregou/não
 *    carregou". Quem precisar do valor chama os getters na hora do uso.
 *
 * Todo consumidor deve chamar `getApiUrl()` / `getSocketUrl()` /
 * `getSupportWebhookUrl()` NA HORA DO USO — nunca capturar num `const` de módulo,
 * senão fica preso no valor de antes do carregamento.
 */
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebaseConfig';

const STORAGE_KEY = 'auuii:appConfig:v1';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h: muda URL → propaga em até meio dia

// .env continua como REDE DE SEGURANÇA (build sem doc/sem permissão/sem internet).
const limpa = (v) => String(v || '').trim().replace(/\/+$/, '');
const urlValida = (v) => typeof v === 'string' && /^https?:\/\/[^\s]+$/i.test(v.trim());

const CONFIG = {
  apiUrl: limpa(process.env.REACT_APP_API_URL),
  chatWebhookUrl: limpa(process.env.REACT_APP_SUPPORT_WEBHOOK_URL),
};

/**
 * DESENVOLVIMENTO: o .env MANDA e o Firestore não sobrescreve.
 *
 * Rodando `npm start` contra o backend do Docker (localhost:3000), sem isto o
 * settings/appConfig traria a URL de PRODUÇÃO e o painel local sairia falando com o
 * servidor real — justamente o oposto de subir o backend na máquina. Então, em dev,
 * cada URL definida no .env vira TRAVA: a config remota é lida, mas não substitui.
 * Em build de produção nada trava e o Firestore continua sendo a fonte.
 *
 * Para testar em dev COM a config remota, basta remover a variável do .env.development.
 */
const EH_DEV = process.env.NODE_ENV === 'development';
const TRAVADAS = new Set(
  EH_DEV ? Object.keys(CONFIG).filter((k) => CONFIG[k]) : []
);
if (TRAVADAS.size > 0) {
  console.info(`[appConfig] dev: usando URL local do .env (${[...TRAVADAS].join(', ')}) — config remota ignorada`);
}

const listeners = new Set();
// Um relógio por doc: o público é lido no boot; o privado só quando há login.
let fetchedAt = { publicConfig: 0, appConfig: 0 };
const fetching = {};      // Promise em voo por doc (evita getDoc duplicado)
let authWatcher = null;

const aplicar = (cfg) => {
  if (!cfg || typeof cfg !== 'object') return false;
  let mudou = false;
  for (const k of ['apiUrl', 'chatWebhookUrl']) {
    if (TRAVADAS.has(k)) continue; // dev apontando pro Docker local: remoto não manda aqui
    if (urlValida(cfg[k]) && limpa(cfg[k]) !== CONFIG[k]) {
      CONFIG[k] = limpa(cfg[k]);
      mudou = true;
    }
  }
  if (mudou) listeners.forEach((fn) => { try { fn(); } catch { /* ouvinte quebrado não derruba os outros */ } });
  return mudou;
};

const lerCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
};

const gravarCache = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...CONFIG,
      savedAt: Date.now(),
      // Guardados separados: o doc público pode ter sido lido sem que o privado
      // (webhook) tenha sido — no próximo boot isso decide o que ainda falta buscar.
      fetchedAt,
    }));
  } catch { /* modo privado / storage cheio: segue só em memória */ }
};

const cacheFresco = (docId) => Date.now() - (fetchedAt[docId] || 0) < CACHE_TTL_MS;

/** UMA leitura de um doc de settings. Reaproveita a Promise se já estiver em voo. */
const buscarRemoto = (docId) => {
  if (fetching[docId]) return fetching[docId];
  fetching[docId] = (async () => {
    try {
      const snap = await getDoc(doc(db, 'settings', docId));
      if (snap.exists()) {
        aplicar(snap.data());
        fetchedAt = { ...fetchedAt, [docId]: Date.now() };
        gravarCache();
        console.info(`[appConfig] configuração remota carregada (${docId})`);
      } else {
        console.info(`[appConfig] doc settings/${docId} não existe — usando padrão`);
      }
    } catch (err) {
      // Sem permissão/offline: fica no cache ou no .env. Nunca é fatal.
      console.info(`[appConfig] configuração remota indisponível (${docId}):`, err?.code || 'erro');
    } finally {
      fetching[docId] = null;
    }
  })();
  return fetching[docId];
};

/**
 * Chamar UMA vez no boot (index.js), antes do render. Semeia do localStorage na hora
 * (síncrono), busca a apiUrl no doc PÚBLICO já — as telas abertas sem login dependem
 * dela — e agenda o doc autenticado (webhook) pra quando houver usuário. Cada busca só
 * acontece se aquele doc estiver ausente ou vencido no cache. Idempotente.
 */
let prontoPublico = null;

export function loadAppConfig() {
  const cache = lerCache();
  if (cache) {
    aplicar(cache);
    // Cache antigo (v1 com `savedAt` só) vale para os dois docs.
    fetchedAt = (cache.fetchedAt && typeof cache.fetchedAt === 'object')
      ? { publicConfig: 0, appConfig: 0, ...cache.fetchedAt }
      : { publicConfig: Number(cache.savedAt) || 0, appConfig: Number(cache.savedAt) || 0 };
  }

  // Sem esperar login: é o que faz landing, cardápio do cliente e /rastreio pegarem
  // a URL nova. Se as regras não permitirem, cai no cache/.env sem quebrar nada.
  prontoPublico = cacheFresco('publicConfig') ? Promise.resolve() : buscarRemoto('publicConfig');

  if (!authWatcher) {
    authWatcher = onAuthStateChanged(auth, (user) => {
      // Logado: o doc autenticado é o único que traz o webhook do suporte.
      if (user && !cacheFresco('appConfig')) buscarRemoto('appConfig');
    });
  }
  return prontoPublico;
}

/**
 * Resolve quando a `apiUrl` do doc público já foi resolvida (ou falhou e valem o cache
 * e o .env). Telas que disparam chamada à API logo no mount SEM login — landing, menu
 * público — devem dar `await` nisto antes de montar a URL, senão a primeira requisição
 * do primeiro acesso pode sair com o valor antigo do build. Nunca rejeita.
 */
export const whenAppConfigReady = () => prontoPublico || Promise.resolve();

/** Base da API (sem barra no fim). */
export const getApiUrl = () => CONFIG.apiUrl;

/** Raiz do socket (/tracking, /chat): API sem o sufixo /api, ou a própria origem. */
export const getSocketUrl = () => {
  const api = CONFIG.apiUrl;
  return api ? api.replace(/\/api\/?$/, '') : window.location.origin;
};

/** Webhook n8n do chat de suporte. */
export const getSupportWebhookUrl = () => CONFIG.chatWebhookUrl;

/** Avisa quando a config muda (ex.: payments.js atualiza o export API_BASE_URL). */
export const onAppConfigChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
